/* ==========================================================================
   DIREVULF — carousel.js

   One paged carousel, used everywhere the reference shows one: the product
   gallery on a phone, and the product rails.

   WHAT IT IS
   ----------
   A scroll-snap track, unchanged. This file does not take over the scrolling
   — the track is a real overflow container, so a swipe, a trackpad, a scroll
   wheel and a screen reader all keep working exactly as they did. What it
   adds is what the reference shows on top: DOTS on a phone and ARROWS on a
   desktop, both derived from the track's own geometry rather than from a
   number this file was told.

   WHY IT MEASURES INSTEAD OF COUNTING
   -----------------------------------
   How many pages a rail has depends on how many cards fit, which depends on
   the viewport. So a page is measured: the track's scrollable width divided
   by one visible width. Change the window and it is measured again. Nothing
   assumes four-up on a desktop and two-up on a phone.

   With JavaScript off, or before this runs, the track still scrolls and the
   controls simply are not there — which is why they are created here rather
   than rendered by Liquid as decoration that might not work.
   ========================================================================== */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function pagesOf(track) {
    /* One page is one visible width. Round outward so a track that scrolls by
       even a few pixels still counts as two pages rather than one. */
    var total = track.scrollWidth;
    var one = track.clientWidth;
    if (!one) return 1;
    /* A rail with a partial last page (three cards at 42% on a phone) is
       still two pages to swipe through, so anything past a small tolerance
       counts as another page. */
    return Math.max(1, Math.ceil(total / one - 0.1));
  }

  function pageOf(track) {
    var one = track.clientWidth;
    if (!one) return 0;
    return Math.round(track.scrollLeft / one);
  }

  /* One ResizeObserver for every rail: read all, then write all. */
  var sharedObserver = window.ResizeObserver ? new window.ResizeObserver(function (entries) {
    var jobs = [];
    for (var i = 0; i < entries.length; i++) {
      var job = entries[i].target.dvCarouselMeasure;
      if (job && jobs.indexOf(job) === -1) jobs.push(job);
    }
    var reads = jobs.map(function (job) { return job.read(); });
    jobs.forEach(function (job, n) { job.write(reads[n]); });
  }) : null;

  function Carousel(root) {
    var track = $('[data-dv-carousel-track]', root);
    if (!track) return null;

    var dotsHost = $('[data-dv-carousel-dots]', root);
    var prev = $('[data-dv-carousel-prev]', root);
    var next = $('[data-dv-carousel-next]', root);
    var label = root.getAttribute('data-dot-label') || 'Go to slide %N%';
    var pages = 1;
    /* Which page the dots are lighting. It is only ever a reflection of where
       the track actually is — never the source of truth for where an arrow
       should go. See stepBy(). */
    var current = 0;

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    /* WHERE THE RAIL IS ALLOWED TO REST.

       The track is `scroll-snap-type: x mandatory` and every card is
       `scroll-snap-align: start`, so the only positions the browser will
       actually hold are the card edges — on a product rail, every 272px.

       This matters because a "page" is one visible width, and one visible
       width is NOT a card edge. Asking for 1195px on a rail whose cards sit
       at 0, 272, 544, 816, 1088 is asking for somewhere the browser refuses
       to stop: it lands the scroll and then drags it to 1088. With
       behavior:'smooth' the snap engine re-evaluates while the animation is
       still running, so the glide pulls one way and the snap pulls the other
       and the rail visibly bounces before settling somewhere that was never
       asked for.

       So targets are chosen from the real snap points instead. The position
       requested is always one the browser is willing to hold, which means
       there is nothing left for it to correct. */
    function snapPoints() {
      var max = maxScroll();
      var base = track.getBoundingClientRect().left - track.scrollLeft;
      var points = [];
      var kids = track.children;
      for (var i = 0; i < kids.length; i++) {
        var at = Math.round(kids[i].getBoundingClientRect().left - base);
        if (at < 0) at = 0;
        if (at > max) at = max;
        if (!points.length || at - points[points.length - 1] > 1) points.push(at);
      }
      if (!points.length) points.push(0);
      if (points[points.length - 1] < max - 1) points.push(max);
      return points;
    }

    function nearestPoint(to) {
      var points = snapPoints();
      var best = points[0];
      var bestGap = Math.abs(points[0] - to);
      for (var i = 1; i < points.length; i++) {
        var gap = Math.abs(points[i] - to);
        if (gap < bestGap) { bestGap = gap; best = points[i]; }
      }
      return best;
    }

    function paintDots() {
      if (!dotsHost) return;
      if (dotsHost.childElementCount === pages) return;
      dotsHost.innerHTML = '';
      if (pages < 2) return;
      for (var i = 0; i < pages; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'dv-dots__dot';
        dot.setAttribute('data-dv-carousel-dot', String(i));
        dot.setAttribute('aria-label', label.split('%N%').join(String(i + 1)));
        dotsHost.appendChild(dot);
      }
    }

    /* READ ONLY. This paints the indicator and the arrows' disabled state and
       does nothing else — it never moves the rail. That is what keeps the
       scroll listener observational: a listener that scrolls would feed its
       own next event and the rail would oscillate.

       `assumeLeft` is the position a control just asked for. Reading the
       scroll position back straight after asking for a smooth scroll would
       give the position it is moving FROM, so a deliberate move paints its
       own answer and the scroll listener confirms it on arrival. */
    function paintState(assumeLeft) {
      var left = typeof assumeLeft === 'number' ? assumeLeft : track.scrollLeft;
      paintWith(left, track.clientWidth || 1, maxScroll());
    }

    /* The writing half of paintState: given the geometry, it only writes. */
    function paintWith(left, one, max) {
      current = left >= max - 1 && pages > 1
        ? pages - 1
        : Math.max(0, Math.min(Math.round(left / one), pages - 1));

      if (dotsHost) {
        $$('[data-dv-carousel-dot]', dotsHost).forEach(function (dot, i) {
          var on = i === current;
          dot.classList.toggle('is-active', on);
          dot.setAttribute('aria-current', on ? 'true' : 'false');
        });
      }

      /* Disabled is decided by the REAL edges of the scroll range, not by the
         page number. A page index is a rounded thing and the rail rarely rests
         on an exact multiple of one width; asking "is there anywhere left to
         go" of the position itself cannot drift out of step with the rail.

         An arrow that cannot go anywhere is disabled rather than hidden, so
         the row does not jump about as a shopper pages through it. */
      if (prev) prev.disabled = left <= 1;
      if (next) next.disabled = left >= max - 1;
      root.classList.toggle('is-paged', pages > 1);
    }

    /* WHERE THE ARROWS SIT, VERTICALLY.

       The arrows are absolutely positioned inside the carousel root, and the
       root is not only the track: it also holds the dots row this file appends.
       So `top: 50%` of the root is NOT the middle of the cards — it drifts down
       by half the dots row, and it MOVES the moment anything in the root
       changes height. Adding the indicator to the product page's rail moved
       those arrows on its own. The product page's own rule went further and
       pinned them at `top: 38%`, a number that is only correct at one card
       height.

       So the anchor is measured from the TRACK and published as a custom
       property the stylesheet reads. The measurement is the track's own centre
       relative to the root, in pixels — not a percentage of anything that can
       change underneath it, and nothing to do with the page's height or scroll
       position. It is re-measured by the ResizeObserver below, which watches
       the track, so cards growing as their images decode moves the anchor with
       them and never leaves it stale. */
    function placeArrows() {
      if (!prev && !next) return;
      var rootBox = root.getBoundingClientRect();
      var trackBox = track.getBoundingClientRect();
      if (!trackBox.height) return;
      var centre = (trackBox.top - rootBox.top) + (trackBox.height / 2);
      root.style.setProperty('--dv-arrow-y', Math.round(centre) + 'px');
    }

    function measure() {
      write(read());
    }

    /* MEASURE IN TWO HALVES. Every rail on the page used to be measured
       straight after the adoption code had inserted its frames, arrows and
       dots, so each rail's first read forced the browser to lay the whole page
       out again — PageSpeed's "forced reflow" in carousel.js, ~100ms on a
       phone for the home page's rails. read() only reads and write() only
       writes; the shared ResizeObserver below reads every rail first and then
       writes every rail, after the browser's own layout, so nothing is forced. */
    function read() {
      var r = {
        pages: pagesOf(track),
        left: track.scrollLeft,
        one: track.clientWidth || 1,
        max: maxScroll(),
        rootTop: 0,
        trackTop: 0,
        trackH: 0
      };
      if (prev || next) {
        var rootBox = root.getBoundingClientRect();
        var trackBox = track.getBoundingClientRect();
        r.rootTop = rootBox.top;
        r.trackTop = trackBox.top;
        r.trackH = trackBox.height;
      }
      return r;
    }

    function write(r) {
      pages = r.pages;
      paintDots();
      paintWith(r.left, r.one, r.max);
      if ((prev || next) && r.trackH) {
        root.style.setProperty('--dv-arrow-y', Math.round((r.trackTop - r.rootTop) + (r.trackH / 2)) + 'px');
      }
    }

    /* Snapping is switched off for the length of a deliberate move and put
       back once the rail has settled. Without that, the snap engine keeps
       re-deciding where the rail belongs on every frame of the animation and
       fights it. Restoring it costs nothing visually because the position it
       is restored at is already a snap point.

       Touch, trackpad and wheel scrolling never come through here, so native
       snapping is untouched for the shopper's own gestures. */
    var settleTimer = null;
    var landTimer = null;

    function resumeSnap() {
      window.clearTimeout(settleTimer);
      settleTimer = null;
      track.style.scrollSnapType = '';
    }

    function settleThenResumeSnap(target) {
      window.clearTimeout(settleTimer);
      var tries = 0;
      (function check() {
        tries++;
        if (Math.abs(track.scrollLeft - target) <= 2 || tries > 40) { resumeSnap(); return; }
        settleTimer = window.setTimeout(check, 50);
      })();
    }

    /* The one operation that moves a rail. Nothing else in this file scrolls
       it, and the scroll listener below only ever reads. */
    function scrollToLeft(left) {
      left = Math.max(0, Math.min(Math.round(left), maxScroll()));

      /* Already there: no animation to start, so a press at either end can
         never produce a twitch. */
      if (Math.abs(left - track.scrollLeft) < 2) { paintState(left); return; }

      track.style.scrollSnapType = 'none';

      var from = track.scrollLeft;

      /* `top` is passed explicitly as the position it already holds, so this
         call cannot alter the vertical scroll of the rail, and it never
         touches the window's. */
      if ('scrollBehavior' in document.documentElement.style) {
        track.scrollTo({ left: left, top: track.scrollTop, behavior: 'smooth' });
      } else {
        track.scrollLeft = left;
      }

      /* A PRESS MUST ALWAYS LAND.

         A smooth scroll is a request the browser is allowed to decline.
         Measured in Chrome on this storefront: a tab sitting behind another
         window reports visibilityState 'hidden', stops running animation
         frames, and a scrollTo({ behavior: 'smooth' }) on this rail then moves
         it by exactly nothing — not deferred until the tab comes back,
         dropped. The press is lost, and the rail is still sitting where it was
         when the shopper looks again. Setting scrollLeft directly is no escape:
         the stylesheet puts scroll-behavior: smooth on the rail, so a plain
         assignment animates too, and is dropped the same way.

         So the request is checked rather than trusted. If the rail has not
         moved a single pixel by the time this runs, the animation never
         started, and the same target is taken instantly instead.

         This cannot produce a second movement: it only fires when nothing
         moved at all, and the position it asks for is the one already asked
         for. A press that arrives while this is pending replaces it, so rapid
         presses still resolve to one landing. */
      window.clearTimeout(landTimer);
      landTimer = window.setTimeout(function () {
        landTimer = null;
        if (track.scrollLeft !== from) return;
        if (Math.abs(left - from) < 2) return;
        try { track.scrollTo({ left: left, top: track.scrollTop, behavior: 'instant' }); }
        catch (e) { track.scrollLeft = left; }
      }, 180);

      paintState(left);
      settleThenResumeSnap(left);
    }

    /* THE ARROWS.

       One click, one target, one scroll. The step is measured from where the
       track ACTUALLY is, so a shopper who swipes and then immediately presses
       an arrow steps from the real position rather than a remembered one, and
       -1 can only ever go left while +1 can only ever go right.

       Rapid presses do not queue: each one recomputes from the live position
       and replaces the previous command. */
    function stepBy(direction) {
      var one = track.clientWidth;
      if (!one) return;

      var here = track.scrollLeft;
      var points = snapPoints();
      var target;
      var i;

      if (direction > 0) {
        /* The furthest card edge still within one screen of here... */
        target = points[0];
        for (i = 0; i < points.length; i++) {
          if (points[i] <= here + one + 1) target = points[i];
        }
        /* ...but always at least one card forward, so a press never stalls. */
        if (target <= here + 1) {
          for (i = 0; i < points.length; i++) {
            if (points[i] > here + 1) { target = points[i]; break; }
          }
        }
      } else {
        target = points[points.length - 1];
        for (i = points.length - 1; i >= 0; i--) {
          if (points[i] >= here - one - 1) target = points[i];
        }
        if (target >= here - 1) {
          for (i = points.length - 1; i >= 0; i--) {
            if (points[i] < here - 1) { target = points[i]; break; }
          }
        }
      }

      scrollToLeft(target);
    }

    /* Jumping to a numbered page: the dots, and Home / End. The page is still
       one visible width, but it lands on the nearest position the rail is
       willing to hold rather than on the raw multiple. */
    function goTo(index) {
      var target = Math.max(0, Math.min(index, pages - 1));
      scrollToLeft(nearestPoint(target * track.clientWidth));
    }

    root.addEventListener('click', function (event) {
      var dot = event.target.closest('[data-dv-carousel-dot]');
      if (dot) { goTo(parseInt(dot.getAttribute('data-dv-carousel-dot'), 10) || 0); return; }
      if (event.target.closest('[data-dv-carousel-prev]')) { stepBy(-1); return; }
      if (event.target.closest('[data-dv-carousel-next]')) { stepBy(1); }
    });

    /* Keyboard. The track is a real scroll container, so arrow keys already
       move it a little; these page it, which is what the dots represent. Only
       when the track itself has focus, so arrow keys inside a card's own
       controls are untouched. Left is previous and right is next, by the same
       measured-from-reality rule the arrow buttons use. */
    track.addEventListener('keydown', function (event) {
      if (event.target !== track) return;
      if (pages < 2) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); stepBy(-1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); stepBy(1); }
      else if (event.key === 'Home') { event.preventDefault(); goTo(0); }
      else if (event.key === 'End') { event.preventDefault(); goTo(pages - 1); }
    });
    if (!track.hasAttribute('tabindex')) track.setAttribute('tabindex', '0');

    /* FOCUS MUST NOT SCROLL THE PAGE.

       The line above makes the track focusable so the rail can be reached and
       paged with the keyboard. That has a side effect: a rail is around 470px
       tall, and when the browser moves focus to an element it scrolls the PAGE
       until that whole element is in view. Measured on the storefront,
       track.focus() moved the window from 716 to 1633 — a 917px vertical jump,
       with no horizontal movement involved at all. That is the page "dancing"
       up and down when an arrow is pressed.

       The browser performs that focus on mousedown, before the click ever
       reaches the handler, so it cannot be undone afterwards. It is taken away
       from the browser instead: focus is set here first, with preventScroll,
       so by the time the default action runs the element is already focused
       and there is nothing left for it to scroll to.

       Keyboard access is untouched — the track keeps its tabindex, Tab still
       reaches it, and a Tab landing (which SHOULD bring the rail into view)
       still scrolls normally, because this only runs for a pointer press. */
    function focusWithoutScrolling(el) {
      if (!el) return;
      try { el.focus({ preventScroll: true }); }
      catch (e) { /* older browser: leave the default alone rather than jump */ }
    }

    track.addEventListener('mousedown', function () {
      focusWithoutScrolling(track);
    });

    /* THE FOCUS RING IS FOR THE KEYBOARD, NOT FOR THE MOUSE.

       Chrome does not draw a focus ring when you click a button with a mouse.
       It does draw one when script moves focus, because it cannot attribute
       that to a pointer — and the line above moves focus by hand. So the theme's
       global ring (store-1.css: 2px solid var(--dv-red), offset 4px, on a
       999px-radius button — a red circle floating around the arrow) appeared on
       every mouse click, which it never did before.

       Rather than take the ring away from everyone, the press is labelled. The
       stylesheet hides the ring only while this attribute is present, and it is
       dropped the moment the shopper touches a key or focus leaves — so a Tab
       landing, and Enter or Space on an already-focused arrow, are indicated
       exactly as before. */
    function dropPointerFocus(event) {
      event.currentTarget.removeAttribute('data-dv-pointer-focus');
    }

    [prev, next].forEach(function (button) {
      if (!button) return;
      button.addEventListener('mousedown', function (event) {
        /* preventDefault stops the browser's own focus-and-scroll; the focus
           is then put back by hand so the arrow still shows a focus ring and
           still works for keyboard and screen-reader users. It does not stop
           the click. */
        event.preventDefault();
        button.setAttribute('data-dv-pointer-focus', '');
        focusWithoutScrolling(button);
      });
      button.addEventListener('keydown', dropPointerFocus);
      button.addEventListener('blur', dropPointerFocus);
    });

    /* Not requestAnimationFrame-gated, for the reason pdp-enhance.js gives on
       the gallery dots: the browser already fires scroll at most once a frame,
       and an rAF gate leaves the indicator — and the arrows' disabled state —
       frozen wherever rAF does not run. A frozen disabled state is how an
       arrow ends up refusing a press it should have accepted.

       This listener READS. It must never scroll: a scroll listener that
       scrolls feeds its own next event, and that is an oscillation. */
    track.addEventListener('scroll', function () { paintState(); }, { passive: true });

    /* The shared observer delivers the first measurement once the browser
       has laid the page out (and again whenever the track changes size), so
       there is no synchronous measure here any more. */
    /* The first measurement is made by mountAll(), for every new rail at
       once (all reads, then all writes). After that the shared observer
       re-measures whenever a track changes size. */
    if (sharedObserver) {
      track.dvCarouselMeasure = { read: read, write: write };
      sharedObserver.observe(track);
    } else {
      window.addEventListener('resize', measure);
    }
    return { measure: measure, read: read, write: write, goTo: goTo, stepBy: stepBy };
  }

  /* ------------------------------------------------------------- adoption */
  /* The rails were written before this component and are adopted rather than
     rewritten: every section keeps its plain, scrolling list, and the controls
     the reference shows are built around it here. That way a rail still works
     with no JavaScript, and no section had to grow markup for a control that
     might never appear.

     `data-dv-rail` is the theme's own marker for a rail of cards. Chip strips
     do not carry it, and are deliberately left scrolling — the reference keeps
     them that way. */
  function strings() {
    var node = $('[data-dv-shop-config]');
    if (!node) return {};
    try { return (JSON.parse(node.textContent).carousel) || {}; }
    catch (e) { return {}; }
  }

  function chevron() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'dv-icon dv-carousel__chev');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttribute('href', '#icon-chevron-right');
    svg.appendChild(use);
    return svg;
  }

  function arrow(direction, label) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'dv-carousel__arrow dv-carousel__arrow--' + direction + ' dv-tap';
    button.setAttribute('data-dv-carousel-' + direction, '');
    if (label) button.setAttribute('aria-label', label);
    button.appendChild(chevron());
    return button;
  }

  function adoptRails(root) {
    var copy = strings();
    $$('[data-dv-rail]', root || document).forEach(function (rail) {
      if (rail.dvAdopted) return;
      if (rail.closest('[data-dv-carousel]')) return;
      if (!rail.parentNode) return;
      rail.dvAdopted = true;

      var frame = document.createElement('div');
      frame.className = 'dv-carousel';
      frame.setAttribute('data-dv-carousel', '');
      if (copy.go_to) frame.setAttribute('data-dot-label', copy.go_to);

      rail.parentNode.insertBefore(frame, rail);
      frame.appendChild(arrow('prev', copy.previous));
      frame.appendChild(rail);
      frame.appendChild(arrow('next', copy.next));

      var dots = document.createElement('div');
      dots.className = 'dv-dots dv-carousel__dots';
      dots.setAttribute('data-dv-carousel-dots', '');
      frame.appendChild(dots);

      rail.setAttribute('data-dv-carousel-track', '');
    });
  }

  /* Rails whose arrows the template already renders. The product page's
     "others also bought" rail is one: sections/product.liquid draws
     .pdp2-also__nav--prev / --next, positioned and styled for that section.

     Those buttons are ADOPTED, not duplicated. Tagging them hands them to the
     same Carousel() that already drives this rail's indicator, so the rail
     ends up with exactly ONE engine. Nothing is added to or moved in the DOM,
     so the arrows keep their position, their size and their colour.

     WHY THIS CHANGED. Until now pdp-enhance.js drove these same buttons with
     its own rail.scrollBy({ left: cardWidth, behavior: 'auto' }) while
     Carousel() was mounted on the same rail for the dots — two systems on one
     element. And behavior:'auto' does not mean "instant": per spec it defers
     to the element's computed scroll-behavior, which the stylesheet sets to
     `smooth` on .pdp2-also__rail. So a press started a SMOOTH animation with
     `scroll-snap-type: x mandatory` still live, and the snap engine re-decided
     the resting place on every frame of that animation and dragged the rail
     back to the card edge it started from. Measured on the storefront: one
     press ran scrollBy({ left: 309 }) and left rail.scrollLeft at 0. The rail
     did not move at all.

     scrollToLeft() above is the path that suspends snapping for the length of
     a deliberate move and asks only for positions the browser is willing to
     hold, so there is nothing left for the snap engine to correct. */
  var ADOPT_IN_PLACE = [
    {
      selector: '.pdp2-also__rail',
      dotsClass: 'dv-dots pdp2-also__dots',
      prev: '.pdp2-also__nav--prev',
      next: '.pdp2-also__nav--next'
    }
  ];

  function adoptDotsOnly(root) {
    var copy = strings();
    ADOPT_IN_PLACE.forEach(function (entry) {
      $$(entry.selector, root || document).forEach(function (rail) {
        if (rail.dvAdopted) return;
        var host = rail.parentNode;
        if (!host) return;
        /* Never adopt a rail that is already inside a mounted carousel. */
        if (rail.closest('[data-dv-carousel]')) return;
        rail.dvAdopted = true;

        rail.setAttribute('data-dv-carousel-track', '');

        var dots = document.createElement('div');
        dots.className = entry.dotsClass;
        dots.setAttribute('data-dv-carousel-dots', '');
        host.appendChild(dots);

        /* The buttons the template already rendered, handed to Carousel().
           Tagging is all it takes: Carousel() looks them up by these
           attributes and drives both the movement and the disabled state. */
        var existingPrev = entry.prev ? $(entry.prev, host) : null;
        var existingNext = entry.next ? $(entry.next, host) : null;
        if (existingPrev) existingPrev.setAttribute('data-dv-carousel-prev', '');
        if (existingNext) existingNext.setAttribute('data-dv-carousel-next', '');

        /* The host becomes the carousel root, and the tagged arrows are inside
           it, so the delegated click handler and the disabled state both
           reach them. */
        if (copy.go_to) host.setAttribute('data-dot-label', copy.go_to);
        host.setAttribute('data-dv-carousel', '');
      });
    });
  }

  /* Swipe rails that have no arrows anywhere (the product page's "Complete
     the fit", the fandom hub's rails). On a phone they get the same dot
     indicator as every other carousel, below the rail; on a desktop the
     shared .dv-dots rules hide it, exactly as before. Each rail gets its own
     frame so a section holding two rails never mixes their dots. */
  var DOTS_ONLY = ['.pdp2-ctf__rail', '.dvh .dvh-rail'];

  function adoptDotsFrames(root) {
    var copy = strings();
    DOTS_ONLY.forEach(function (selector) {
      $$(selector, root || document).forEach(function (rail) {
        if (rail.dvAdopted || rail.closest('[data-dv-carousel]') || !rail.parentNode) return;
        rail.dvAdopted = true;
        var frame = document.createElement('div');
        frame.className = 'dv-carousel dv-carousel--dots';
        frame.setAttribute('data-dv-carousel', '');
        if (copy.go_to) frame.setAttribute('data-dot-label', copy.go_to);
        rail.parentNode.insertBefore(frame, rail);
        frame.appendChild(rail);
        var dots = document.createElement('div');
        dots.className = 'dv-dots dv-carousel__dots';
        dots.setAttribute('data-dv-carousel-dots', '');
        frame.appendChild(dots);
        rail.setAttribute('data-dv-carousel-track', '');
      });
    });
  }

  function mountAll(root) {
    adoptRails(root);
    adoptDotsOnly(root);
    adoptDotsFrames(root);
    var fresh = [];
    $$('[data-dv-carousel]', root || document).forEach(function (node) {
      if (node.dvCarousel) return;
      node.dvCarousel = Carousel(node) || true;
      if (node.dvCarousel !== true) fresh.push(node.dvCarousel);
    });
    /* One layout for all of them: read every new rail, then write every new
       rail. Measuring each rail as it was built used to force a fresh layout
       of the page per rail. */
    var reads = fresh.map(function (c) { return c.read(); });
    fresh.forEach(function (c, i) { c.write(reads[i]); });
  }

  window.DirevulfCarousel = { mount: mountAll };

  /* Rails are filled after the fact by the Section Rendering API, so mounting
     runs again whenever a section announces that it has loaded. */
  document.addEventListener('dv:section:loaded', function () { mountAll(); });

  /* Rails that scripts build after load (the fandom hub renders its sections
     from JSON) are picked up when they appear. One pass per frame at most;
     mountAll() skips anything already mounted, so its own insertions end the
     cycle. */
  var pendingMount = false;
  function watch() {
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function (records) {
      if (pendingMount) return;
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) {
          pendingMount = true;
          /* setTimeout, not requestAnimationFrame: rAF does not run in a
             background tab, and a rail built there must still get its dots. */
          setTimeout(function () { pendingMount = false; mountAll(); }, 60);
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mountAll(); watch(); });
  } else {
    mountAll();
    watch();
  }
})();
